/**
 * STORY-037: Discussions IA multi-canal
 * Liste des conversations IA avec toggle activer/desactiver
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Bot,
  ChevronRight,
  Globe,
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  Power,
  Search,
  Send,
  User,
  X,
} from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { EmptyState } from '@/components/ui/EmptyState'
import { useAIConversations, useAIConversation, useToggleAI, useSendMessage } from '@/hooks/useAI'
import {
  CHANNEL_LABELS,
  CONVERSATION_STATUS_LABELS,
  PROJECT_TYPE_LABELS,
  type ConversationChannel,
  type ConversationStatus,
} from '@/types/ai'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'

// Channel icons
const CHANNEL_ICONS: Record<ConversationChannel, typeof Globe> = {
  web: Globe,
  whatsapp: Phone,
  email: Mail,
}

// Status colors
const STATUS_VARIANTS: Record<ConversationStatus, 'mint' | 'gold' | 'gray' | 'blue'> = {
  active: 'mint',
  pending_intervention: 'gold',
  resolved: 'gray',
  archived: 'gray',
}

export default function AIConversationsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<ConversationStatus | ''>('')
  const [channelFilter, setChannelFilter] = useState<ConversationChannel | ''>('')
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)
  const [messageInput, setMessageInput] = useState('')

  const debouncedSearch = useDebounce(search, 300)

  const { data: conversations, isLoading } = useAIConversations({
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    channel: channelFilter || undefined,
  })

  const { data: selectedConversation, isLoading: isLoadingConversation } = useAIConversation(
    selectedConversationId || undefined
  )

  const toggleAI = useToggleAI()
  const sendMessage = useSendMessage()

  const handleToggleAI = (conversationId: string, enabled: boolean) => {
    toggleAI.mutate({ conversationId, enabled })
  }

  const handleSendMessage = () => {
    if (!selectedConversationId || !messageInput.trim()) return

    sendMessage.mutate({
      conversationId: selectedConversationId,
      message: messageInput,
    })
    setMessageInput('')
  }

  const handleViewQuestionnaire = (conversationId: string) => {
    navigate(`/ai/questionnaire/${conversationId}`)
  }

  const breadcrumbItems = [{ label: 'Conversations IA' }]

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Conversations IA
          </h1>
          <p className="mt-1 text-brown-600">
            Gerez les conversations automatisees avec vos clients
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="mint" size="md">
            {conversations?.filter((c) => c.status === 'active').length || 0} actives
          </Badge>
          <Badge variant="gold" size="md">
            {conversations?.filter((c) => c.status === 'pending_intervention').length || 0} en attente
          </Badge>
        </div>
      </div>

      {/* Filters */}
      <Card variant="bordered" padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brown-400" />
              <Input
                placeholder="Rechercher par nom ou email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ConversationStatus | '')}
              className="w-40"
            >
              <option value="">Tous les statuts</option>
              {Object.entries(CONVERSATION_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value as ConversationChannel | '')}
              className="w-40"
            >
              <option value="">Tous les canaux</option>
              {Object.entries(CHANNEL_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </Card>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversations list */}
        <div className="lg:col-span-1 space-y-4">
          <h2 className="text-sm font-medium text-brown-500 uppercase tracking-wide">
            Conversations ({conversations?.length || 0})
          </h2>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : conversations?.length === 0 ? (
            <EmptyState
              icon={<MessageSquare className="w-12 h-12" />}
              title="Aucune conversation"
              description="Les nouvelles conversations apparaitront ici"
            />
          ) : (
            <div className="space-y-2">
              {conversations?.map((conversation) => {
                const ChannelIcon = CHANNEL_ICONS[conversation.channel]
                const isSelected = selectedConversationId === conversation.id

                return (
                  <motion.div
                    key={conversation.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card
                      variant={isSelected ? 'bordered' : 'default'}
                      padding="sm"
                      className={cn(
                        'cursor-pointer transition-all hover:shadow-md',
                        isSelected && 'ring-2 ring-mint-500 border-mint-500'
                      )}
                      onClick={() => setSelectedConversationId(conversation.id)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={cn(
                              'w-10 h-10 rounded-full flex items-center justify-center',
                              conversation.aiEnabled ? 'bg-mint-100' : 'bg-beige-100'
                            )}
                          >
                            <ChannelIcon
                              className={cn(
                                'w-5 h-5',
                                conversation.aiEnabled ? 'text-mint-600' : 'text-brown-400'
                              )}
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-brown-900 truncate">
                              {conversation.clientName || conversation.clientEmail}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <Badge variant={STATUS_VARIANTS[conversation.status]} size="sm">
                                {CONVERSATION_STATUS_LABELS[conversation.status]}
                              </Badge>
                              {conversation.projectType && (
                                <span className="text-xs text-brown-500 truncate">
                                  {PROJECT_TYPE_LABELS[conversation.projectType]}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleToggleAI(conversation.id, !conversation.aiEnabled)
                            }}
                            className={cn(
                              'p-1.5 rounded-lg transition-colors',
                              conversation.aiEnabled
                                ? 'bg-mint-100 text-mint-600 hover:bg-mint-200'
                                : 'bg-beige-100 text-brown-400 hover:bg-beige-200'
                            )}
                            title={conversation.aiEnabled ? 'Desactiver l\'IA' : 'Activer l\'IA'}
                          >
                            <Power className="w-4 h-4" />
                          </button>
                          <span className="text-xs text-brown-400">
                            {conversation.lastMessageAt
                              ? new Date(conversation.lastMessageAt).toLocaleDateString('fr-FR')
                              : '-'}
                          </span>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                )
              })}
            </div>
          )}
        </div>

        {/* Conversation detail */}
        <div className="lg:col-span-2">
          {selectedConversationId ? (
            isLoadingConversation ? (
              <Card className="h-[600px] flex items-center justify-center">
                <Spinner size="lg" />
              </Card>
            ) : selectedConversation ? (
              <Card variant="bordered" padding="none" className="h-[600px] flex flex-col">
                {/* Conversation header */}
                <div className="p-4 border-b border-beige-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-mint-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-mint-600" />
                    </div>
                    <div>
                      <p className="font-medium text-brown-900">
                        {selectedConversation.clientName || selectedConversation.clientEmail}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-brown-500">
                        <Badge variant={STATUS_VARIANTS[selectedConversation.status]} size="sm">
                          {CONVERSATION_STATUS_LABELS[selectedConversation.status]}
                        </Badge>
                        <span className="flex items-center gap-1">
                          <Bot className="w-3 h-3" />
                          IA {selectedConversation.aiEnabled ? 'active' : 'inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewQuestionnaire(selectedConversation.id)}
                    >
                      Voir le questionnaire
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedConversationId(null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {selectedConversation.messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-brown-400">
                      <div className="text-center">
                        <MessageCircle className="w-12 h-12 mx-auto mb-2" />
                        <p>Aucun message dans cette conversation</p>
                      </div>
                    </div>
                  ) : (
                    selectedConversation.messages.map((message) => (
                      <div
                        key={message.id}
                        className={cn(
                          'flex',
                          message.role === 'user' ? 'justify-end' : 'justify-start'
                        )}
                      >
                        <div
                          className={cn(
                            'max-w-[80%] rounded-lg p-3',
                            message.role === 'user'
                              ? 'bg-mint-600 text-white'
                              : message.role === 'assistant'
                              ? 'bg-beige-100 text-brown-900'
                              : 'bg-gold-100 text-gold-800 text-center w-full'
                          )}
                        >
                          {message.role !== 'user' && (
                            <div className="flex items-center gap-1 mb-1 text-xs opacity-75">
                              {message.role === 'assistant' ? (
                                <>
                                  <Bot className="w-3 h-3" />
                                  Assistant IA
                                </>
                              ) : (
                                'Systeme'
                              )}
                            </div>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                          <p
                            className={cn(
                              'text-xs mt-1',
                              message.role === 'user' ? 'text-mint-200' : 'text-brown-400'
                            )}
                          >
                            {new Date(message.timestamp).toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Message input */}
                <div className="p-4 border-t border-beige-200">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Ecrire un message (comme l'IA)..."
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          handleSendMessage()
                        }
                      }}
                      disabled={sendMessage.isPending}
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={!messageInput.trim() || sendMessage.isPending}
                    >
                      {sendMessage.isPending ? (
                        <Spinner size="sm" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-brown-400 mt-2">
                    Appuyez sur Entree pour envoyer. Les messages seront traites par l'IA si elle est activee.
                  </p>
                </div>
              </Card>
            ) : (
              <Card className="h-[600px] flex items-center justify-center text-brown-400">
                Conversation non trouvee
              </Card>
            )
          ) : (
            <Card className="h-[600px] flex items-center justify-center">
              <div className="text-center text-brown-400">
                <MessageSquare className="w-12 h-12 mx-auto mb-4" />
                <p className="text-lg font-medium">Selectionnez une conversation</p>
                <p className="text-sm mt-1">
                  Cliquez sur une conversation pour voir les details
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
